/**
 * @module ConstructorIO/helpers
 */

import {buildRelativeURL, generateSizeMap, getWidthType, mapGenderAttribute, getCountryPath} from '../../spa/helper';
import ConstructorIOProduct from '../product';
import util from '../../utils/util';
import {getProductURL} from './url';

const SELECTED = 'selected';

/**
 * @param {Object} groups - Constructor categories
 * @returns {Array<import('../../spa/type-definitions').Category>} processed categories
 */
export const processCategories = groups => {
    const categories = groups.map(group => ({
        id: group.group_id,
        name: group.display_name,
        count: group.count,
        data: {...group.data, url: buildRelativeURL(group.data?.url)},
        children: group.children.map(child => ({
            id: child.group_id,
            name: child.display_name,
            count: child.count,
            data: {...child.data, url: buildRelativeURL(child.data?.url)}
        })),
        parents: group.parents.map(parent => ({
            name: parent.display_name,
            id: parent.group_id
        }))
    }));

    return categories;
};

/**
 * @param {Object} refs - Constructor refinements
 * @returns {import('../../spa/type-definitions').RefinementsData} processed refinements
 */
export const processRefinements = refs => {
    const refinements = refs
        .filter(refinement => {
            if (refinement.name.includes('_')) {
                if (refinement.name.includes(`_${window.CURRENT_COUNTRY}`)) {
                    return true;
                }
                return false;
            }
            return true;
        })
        .map(refinement => ({
            id: refinement.name,
            baseID: refinement.name.split('_')[0],
            name: refinement.display_name,
            selected: false,
            options: refinement.options.map(option => ({
                name: option.display_name,
                count: option.count,
                selected: option.status === SELECTED,
                value: option.value,
                strippedValue: option.value.toString().replace(/"/g, '')
            }))
        }));

    refinements.forEach(refinement => {
        if (refinement.options.find(option => option.selected)) {
            refinement.selected = true;
        }

        const lowerCaseRefinementID = refinement.id.toLowerCase();
        if (lowerCaseRefinementID.includes('size')) {
            refinement.sizeMap = generateSizeMap(refinement.options);
            refinement.isSizeRefinement = true;
        } else if (lowerCaseRefinementID.includes('price')) {
            refinement.isPriceRefinement = true;
        } else if (lowerCaseRefinementID.includes('width')) {
            refinement.isWidthRefinement = true;
            refinement.options.forEach(option => {
                option.widthPresentation = getWidthType(option.value);
            });
        } else if (lowerCaseRefinementID.includes('color')) {
            refinement.isColorRefinement = true;
        } else if (lowerCaseRefinementID.includes('gender')) {
            refinement.isGenderRefinement = true;
            refinement.options.forEach(option => {
                option.genderPresentation = mapGenderAttribute(refinement);
            });
        } else if (lowerCaseRefinementID.includes('model')) {
            refinement.isModelRefinement = true;
        }
    });
    const selectedRefinements = refinements.filter(refinement => refinement.selected);

    return {
        selectedRefinements,
        refinements
    };
};

/**
 * @param {Array<Object>} sortOptions - Constructor sorting options
 * @returns {import('../../spa/type-definitions').SortingOptions} processed sorting options
 */
export const processSortingOptions = sortOptions => {
    const options = sortOptions
        .filter(sRule => {
            if (sRule.sort_by.includes('_') && sRule.sort_by !== '__cnstrc_release_time') {
                return !!sRule.sort_by.includes(`_${window.CURRENT_COUNTRY}`);
            }
            return true;
        })
        .map(sRule => ({
            id: sRule.sort_by,
            name: sRule.display_name,
            selected: sRule.status === SELECTED,
            order: sRule.sort_order
        }));
    const selectedOption = options.find(sRule => sRule.selected) || null;

    return {selectedOption, options};
};

/**
 * @param {Object} products - Constructor results
 * @returns {Array<Object>} products
 */
export const processProductSearchResults = products => {
    const countryPath = getCountryPath();

    return products.map(result => {
        const product = new ConstructorIOProduct(result);
        const url = getProductURL(result.variations_map, product, countryPath);

        const swatchesLocalizedData = product.get('swatches');

        const data = {
            url,
            name: product.get('value'),
            UUID: product.get('UUID'),
            ID: product.get('id'),
            flags: product.get('flags'),
            swatches: product.get('swatchesBaseInfo'),
            comparePriceString: product.get('comparePriceString'),
            mainProductGroup: product.get('mainProductGroup'),
            sizeAndUnit: product.get('sizeAndUnit'),
            promotions: product.get('promotions'),
            parentName: product.get('parentName') || product.get('value'),
            modelName: product.get('modelName'),
            shortName: product.get('shortName'),
            colorName: product.get('colorName'),
            productCharacteristics: product.get('productCharacteristics'),
            priceInfo: product.get('priceInfo'),
            additionalInfo: product.get('additionalInfo'),
            image: {
                alt: product.get('image:tile')?.alt,
                title: product.get('image:tile')?.title,
                url: product.get('image:tile')?.url
            },
            hoverImage: {
                alt: product.get('image:hover')?.alt,
                title: product.get('image:hover')?.title,
                url: product.get('image:hover')?.url
            },
            variationsMap: product.get('variationsMap'),
            variationId: product.get('variation_id')
        };
        mergeSwatchData(data, swatchesLocalizedData);
        getPriceInfoFromSelectedSwatch(data);
        if (data.mainProductGroup === 3) {
            data.priceInfo.isCosmetics = true;
        }
        return data;
    });
};

/**
 *  Process data returned from constructor to useful for us data
 *
 * @param {Object} rawProduct product data from constructor result
 * @returns {Object} processed data
 */
export const processQuizProduct = rawProduct => {
    const product = new ConstructorIOProduct(rawProduct);
    const swatchesLocalizedData = product.get('swatches');
    const data = {
        swatches: product.get('swatchesBaseInfo'),
        priceInfo: product.get('priceInfo')
    };

    mergeSwatchData(data, swatchesLocalizedData);
    getPriceInfoFromSelectedSwatch(data);

    return data;
};

/**
 * Get price info from selected swatch, because we don't save it separetely if there is swatches
 *
 * @param {Object} data product data
 */
function getPriceInfoFromSelectedSwatch(data) {
    if (data.swatches?.length) {
        const selectedSwatch = data.swatches.find(swatch => swatch.preselectCurrentSwatch === true);

        if (selectedSwatch?.priceInfo) {
            try {
                data.priceInfo = JSON.parse(selectedSwatch.priceInfo);
            } catch (e) {
                data.priceInfo = {};
            }
        }
    }
}

/**
 * Merge swatches base data and swatches localized data
 *
 * @param {Array} data all product data
 * @param {Array} swatchesLocalizedData  swathes localized data
 */
function mergeSwatchData(data, swatchesLocalizedData) {
    if (data.swatches === null) {
        // make old format of product feed to work, if for some reason product is not update in constructor
        data.swatches = swatchesLocalizedData;
    } else if (swatchesLocalizedData) {
        const swatchesLocalizedDataKeys = {};

        swatchesLocalizedData.forEach(element => {
            swatchesLocalizedDataKeys[element.colorID] = element;
        });
        data.swatches = data.swatches.map(swatch => {
            if (swatchesLocalizedDataKeys[swatch.colorID]) {
                return {...swatch, ...swatchesLocalizedDataKeys[swatch.colorID]};
            }
            return swatch;
        });
    }
}

/**
 * @param {Object} products - Constructor results
 * @returns {Array<Object>} products
 */
export const processProductSearchSuggestions = products => {
    const countryPath = getCountryPath();

    return products.map(result => {
        const product = new ConstructorIOProduct(result);
        const url = getProductURL(result.variations_map, product, countryPath);

        const swatchesLocalizedData = product.get('swatches');
        const mainProductGroup = product.get('mainProductGroup');

        const data = {
            url,
            ID: product.get('id'),
            name: product.get('value'),
            parentName: product.get('parentName') || product.get('value'),
            colorName: product.get('colorName'),
            modelName: product.get('modelName'),
            shortName: product.get('shortName'),
            swatches: product.get('swatchesBaseInfo'),
            priceInfo: product.get('priceInfo'),
            additionalInfo: product.get('additionalInfo'),
            image: {
                alt: product.get('image:suggest')?.alt,
                title: product.get('image:suggest')?.title,
                url: product.get('image:suggest')?.url
            }
        };
        mergeSwatchData(data, swatchesLocalizedData);
        getPriceInfoFromSelectedSwatch(data);
        if (mainProductGroup === 3) {
            data.priceInfo.isCosmetics = true;
        }
        return data;
    });
};

/**
 * @param {Object} terms - Constructor results
 * @returns {Array<Object>} terms
 */
export const processTermsSearchSuggestions = terms => {
    return terms.map(result => {
        return {
            value: result.value,
            url: util.appendParamToURL(window.Urls.search, 'q', result.value)
        };
    });
};

/**
 * @param {Object} products - Constructor results
 * @returns {Array<Object>} products
 */
export const processProductRecommendations = products => {
    const countryPath = getCountryPath();

    return products
        .map(result => {
            const product = new ConstructorIOProduct(result);
            const url = getProductURL(result.variations_map, product, countryPath);

            const swatchesLocalizedData = product.get('swatches');

            const data = {
                url,
                UUID: product.get('UUID'),
                ID: product.get('id'),
                name: product.get('value'),
                parentName: product.get('parentName') || product.get('value'),
                mainProductGroup: product.get('mainProductGroup'),
                colorName: product.get('colorName'),
                modelName: product.get('modelName'),
                shortName: product.get('shortName'),
                flags: product.get('flags'),
                swatches: product.get('swatchesBaseInfo'),
                priceInfo: product.get('priceInfo'),
                comparePriceString: product.get('comparePriceString'),
                sizeAndUnit: product.get('sizeAndUnit'),
                variationId: product.get('variation_id'),
                strategyId: result.strategy?.id,
                additionalInfo: product.get('additionalInfo'),
                image: {
                    alt: product.get('image:tile')?.alt,
                    title: product.get('image:tile')?.title,
                    url: product.get('image:tile')?.url
                },
                variationsMap: product.get('variationsMap')
            };
            mergeSwatchData(data, swatchesLocalizedData);
            getPriceInfoFromSelectedSwatch(data);
            if (data.mainProductGroup === 3) {
                data.priceInfo.isCosmetics = true;
            }
            return data;
        })
        .filter(product => {
            if (product.additionalInfo?.isComingSoon || product.additionalInfo?.searchableIfUnavailable) {
                return false;
            }
            return true;
        });
};
